export type ID = string;
export type Millimetres = number;
export type ProductViewId = 'front' | 'back' | 'left-sleeve' | 'right-sleeve';
export type PrintTechnology = 'dtf' | 'transfer' | 'dtg' | 'screen-print';

export interface MmSize {
  width: Millimetres;
  height: Millimetres;
}

export interface MmPoint {
  x: Millimetres;
  y: Millimetres;
}

export interface MmRect extends MmPoint, MmSize {}

export interface InsetsMm {
  top: Millimetres;
  right: Millimetres;
  bottom: Millimetres;
  left: Millimetres;
}

export interface ProductColour {
  id: ID;
  name: string;
  hex: `#${string}`;
}

export interface ProductVariant {
  id: ID;
  /** Previous IDs accepted when older drafts or admin payloads are opened. */
  legacyIds?: ID[];
  label: string;
  audience: 'men' | 'women' | 'kids' | 'unisex';
  /** Optional variant-specific garment photography keyed by printable side. */
  mockupImages?: Partial<Record<ProductViewId, string>>;
  /** Scales the visible design frame to match a narrower or shorter garment cut. */
  previewAreaScale?: { x: number; y: number };
  /** Omit when the complete product palette is available for this variant. */
  colourIds?: ID[];
  /** Variant-specific default; falls back to the product default colour. */
  defaultColourId?: ID;
  /** Complete size range configured for this catalogue product. */
  sizes: string[];
  /** Customer-selectable subset. Omit when every configured size is available. */
  selectableSizes?: string[];
  /** Preferred initially selected size. Falls back to M or the first selectable size. */
  defaultSize?: string;
  /** Physical profiles live with their variant in the canonical product JSON. */
  sizeProfiles?: ProductSizeProfile[];
  priceDeltaCents?: number;
}

/** Measurements taken from a garment laid flat, in millimetres. */
export interface GarmentMeasurementsMm {
  /** Half-chest / garment width measured horizontally on a flat garment. */
  flatWidth: Millimetres;
  bodyLength: Millimetres;
  sleeveLength: Millimetres;
}

export interface GarmentMeasurementSource {
  brand: string;
  model: string;
  productCode: string;
  url: string;
  method: 'lay-flat';
}

export interface ProductSizeSurfaceGeometry {
  /** Torso placement canvas. This is not the physical printer sheet. */
  placementAreaMm: MmSize;
  /** Optional approved transfer/press limit. Placement geometry is used while calibration is open. */
  maxTransferSizeMm?: MmSize;
}

export interface ProductSizeProfile {
  id: ID;
  size: string;
  garmentMm: GarmentMeasurementsMm;
  source: GarmentMeasurementSource;
  surfaces: Partial<Record<ProductViewId, ProductSizeSurfaceGeometry>>;
}

export interface PreviewTransform {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotationDeg: number;
}

export interface PrintableAreaConfig {
  viewId: ProductViewId;
  label: string;
  sizeMm: MmSize;
  safeZoneMm: InsetsMm;
  bleedMm: InsetsMm;
  maxPrintSizeMm: MmSize;
  mockupImage: string;
  garmentMask?: string;
  previewArea: { xPct: number; yPct: number; widthPct: number; heightPct: number };
  previewTransform?: PreviewTransform;
  supportedTechnologies: PrintTechnology[];
}

export interface ProductionRules {
  minimumDpi: number;
  recommendedDpi: number;
  minimumTextHeightMm: number;
  minimumDetailMm: number;
  allowTransparency: boolean;
  acceptedColourModes: Array<'srgb' | 'rgb' | 'cmyk' | 'unknown'>;
  /** Print-and-cut handoff understood by the workshop RIP. */
  workflow?: {
    kind: 'print-cut-transfer';
    machine: string;
    rip: string;
    maximumMediaWidthMm: Millimetres;
    maximumPrintCutWidthMm: Millimetres;
    cutContourSpotName: string;
    pagePaddingMm: Millimetres;
    contourOffsetMm: Millimetres;
    contourResolutionPxPerMm: number;
    minimumContourAreaMm2: number;
    mirror: boolean;
  };
  /** Known heat-press platen. Omitted until measured by the workshop. */
  pressPlatenMm?: MmSize;
  /** True until the usable bounds are measured on the real production equipment. */
  requiresProductionCalibration?: boolean;
}

export interface ProductConfig {
  schemaVersion: 2;
  id: ID;
  name: string;
  slug: string;
  /** Eliadmin record slug used by the saved-configuration API. */
  configuratorSlug?: string;
  garmentType: 't-shirt' | 'hoodie' | 'long-sleeve';
  description: string;
  basePriceCents: number;
  currency: 'EUR';
  defaultColourId: ID;
  colours: ProductColour[];
  variants: ProductVariant[];
  printableAreas: PrintableAreaConfig[];
  productionRules: ProductionRules;
  supportedTechnologies: PrintTechnology[];
}

export interface ObjectTransform {
  /** World-space position of the object's local top-left origin; rotation keeps this origin fixed. */
  positionMm: MmPoint;
  sizeMm: MmSize;
  rotationDeg: number;
  flipX: boolean;
  flipY: boolean;
}

export interface ImageAdjustments {
  brightness: number;
  contrast: number;
  saturation: number;
  sharpness: number;
  grayscale: boolean;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * One alpha-aware outline around an image. Layers are stored from the image
 * outwards, so the first item touches the artwork and the third is outermost.
 */
export interface ImageBorder {
  colour: string;
  widthMm: Millimetres;
}

/** Removes a selected raster colour non-destructively before border composition. */
export interface ImageTransparentColour {
  colour: string;
  /** Normalized RGB distance from the selected colour (0 = exact, 1 = maximum). */
  tolerance: number;
}

export interface BaseDesignObject {
  id: ID;
  name: string;
  type: 'image' | 'text' | 'shape' | 'drawing';
  transform: ObjectTransform;
  opacity: number;
  visible: boolean;
  locked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DesignImageObject extends BaseDesignObject {
  type: 'image';
  assetId: ID;
  crop: CropRect;
  mask?: { kind: 'circle' | 'rounded-rect'; radiusMm?: number };
  transparentColour?: ImageTransparentColour;
  /** Up to three stacked, alpha-aware borders. Optional for legacy designs. */
  borders?: ImageBorder[];
  adjustments: ImageAdjustments;
  versionOf?: ID;
  aiOperationId?: ID;
}

export interface TextStroke {
  colour: string;
  widthMm: number;
}

export interface TextShadow {
  colour: string;
  blurMm: number;
  offsetMm: MmPoint;
}

export interface DesignTextObject extends BaseDesignObject {
  type: 'text';
  text: string;
  fontFamily: string;
  fontAssetId: ID;
  fontSizeMm: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  colour: string;
  align: 'left' | 'center' | 'right';
  lineHeight: number;
  letterSpacingMm: number;
  stroke?: TextStroke;
  shadow?: TextShadow;
  curve?: { radiusMm: number; direction: 'up' | 'down' };
}

export type DesignShapeKind =
  | 'rectangle'
  | 'ellipse'
  | 'triangle'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'star'
  | 'heart'
  | 'arrow';

export interface DesignShapeObject extends BaseDesignObject {
  type: 'shape';
  shape: DesignShapeKind;
  fill: string;
  stroke?: TextStroke;
  cornerRadiusMm?: number;
}

/**
 * One freehand sample in local object coordinates. Keeping points normalized
 * makes a stroke resolution independent while the object transform remains in
 * real-world millimetres.
 */
export interface DrawingPoint {
  x: number;
  y: number;
}

export interface DesignDrawingObject extends BaseDesignObject {
  type: 'drawing';
  /** Local coordinates in the inclusive 0..1 range, in capture order. */
  points: DrawingPoint[];
  /** Physical output width; it does not depend on preview resolution. */
  stroke: TextStroke;
  /** Curve interpolation strength: 0 is a polyline and 1 is maximally smooth. */
  smoothing: number;
}

export type DesignObject =
  | DesignImageObject
  | DesignTextObject
  | DesignShapeObject
  | DesignDrawingObject;

export type AssetTransparency = 'present' | 'absent' | 'unknown';

export interface AssetRecord {
  id: ID;
  kind: 'image' | 'font';
  name: string;
  mimeType: string;
  sourceUrl: string;
  checksum?: string;
  pixelSize?: { width: number; height: number };
  colourMode?: 'srgb' | 'rgb' | 'cmyk' | 'unknown';
  /** Detected from source bytes; absent on legacy and font assets. */
  transparency?: AssetTransparency;
  embedded: boolean;
  license?: string;
}

export type SurfacePrintEffectKind = 'distress' | 'washed' | 'grain' | 'cracks';

export interface SurfacePrintEffectSetting {
  intensity: number;
  seed: number;
}

export interface SurfacePrintEffects {
  algorithmVersion: 1;
  distress: SurfacePrintEffectSetting;
  washed: SurfacePrintEffectSetting;
  grain: SurfacePrintEffectSetting;
  cracks: SurfacePrintEffectSetting;
}

export interface PrintSurfaceDesign {
  viewId: ProductViewId;
  printAreaMm: MmSize;
  objects: DesignObject[];
  /** Optional for backwards compatibility with schema-version 1 documents. */
  printEffects?: SurfacePrintEffects;
}

export interface DesignDocument {
  schemaVersion: 1;
  id: ID;
  productId: ID;
  variantId: ID;
  size: string;
  /** Number of identical garments in this order configuration. */
  quantity: number;
  colourId: ID;
  technology: PrintTechnology;
  surfaces: Partial<Record<ProductViewId, PrintSurfaceDesign>>;
  assets: Record<ID, AssetRecord>;
  acknowledgedWarnings: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ViewportState {
  zoom: number;
  panPx: { x: number; y: number };
}

export interface EditorState {
  design: DesignDocument;
  activeViewId: ProductViewId;
  selectedObjectIds: ID[];
  viewports: Record<ProductViewId, ViewportState>;
}

export interface FontDefinition {
  id: ID;
  family: string;
  label: string;
  weights: number[];
  styles: Array<'normal' | 'italic'>;
  sourceUrl: string;
  extendedSourceUrl?: string;
  italicSourceUrl?: string;
  italicExtendedSourceUrl?: string;
  variableWeightRange?: { min: number; max: number };
  category?: 'sans-serif' | 'serif' | 'display' | 'handwriting';
  license: string;
  fallback: string;
}

export const DEFAULT_VIEWPORT: ViewportState = { zoom: 1, panPx: { x: 0, y: 0 } };

export const createId = (prefix = 'id'): ID =>
  `${prefix}_${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
